import { useCallback, useEffect, useRef, useState } from 'react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { ValveSummary } from '../../../domain/Irrigation';

const POLL_INTERVAL_MS = 30 * 1000;

// Las válvulas de una estación con su estado (regando, último riego,
// horario) y los botones de regar/detener. Lo usan el home y el detalle de
// la estación (a través de StationValves).
export function useStationValves(deviceId: string) {
  const { devices: deviceRepo, irrigation } = useRepositories();
  const [valves, setValves] = useState<ValveSummary[]>([]);
  const [loading, setLoading] = useState(false);
  // Canal de la válvula que se está abriendo/cerrando.
  const [busyChannel, setBusyChannel] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (showSpinner: boolean) => {
      const requestId = ++requestIdRef.current;
      if (showSpinner) setLoading(true);
      try {
        const { valves: list } = await deviceRepo.listValves(deviceId);
        if (requestIdRef.current === requestId) setValves(list);
      } catch {
        // El estado de riego es secundario: si falla se muestra el resto igual.
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [deviceId, deviceRepo]
  );

  useEffect(() => {
    setError(null);
    load(true);
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') load(false);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const run = async (channel: number, action: 'open' | 'close') => {
    setBusyChannel(channel);
    setError(null);
    try {
      if (action === 'open') await irrigation.open(deviceId, channel);
      else await irrigation.close(deviceId, channel);
      await load(false);
    } catch (err) {
      const fallback = action === 'open' ? 'No se pudo iniciar el riego.' : 'No se pudo detener el riego.';
      setError(err instanceof Error ? err.message : fallback);
    } finally {
      setBusyChannel(null);
    }
  };

  return {
    valves,
    loading,
    busyChannel,
    error,
    open: (channel: number) => run(channel, 'open'),
    close: (channel: number) => run(channel, 'close'),
    reload: () => load(false),
  };
}
